import React, {useEffect, useState} from "react";
import Page from "../../../layout/Page";
import {useNavigate, useParams} from "react-router-dom";
import subscriptionService from "../../../service/SubscriptionService";
import Container from "@mui/material/Container";
import {useTranslation} from "react-i18next";
import PageActions from "../../../components/PageActions";
import {useForm} from "react-hook-form";
import {MessageType} from "../../../util/Enums";
import useMessage from "../../../util/useMessage";
import BackButton from "../../../ui/BackButton";
import SaveButton from "../../../ui/SaveButton";
import Panel from "../../../ui/Panel";
import Input from "../../../ui/Input";
import TranslatableFields from "../../../ui/TranslatableFields";
import ApplicationInput from "../../../ui/ApplicationInput";
import Select from "../../../ui/Select";
import MenuItem from "@mui/material/MenuItem";
import ImagePickerButton from "../../../ui/ImagePickerButton";

const SubscriptionEditPage = ({title}) => {

    const param = useParams();
    const subscriptionId = param?.id;

    const {t} = useTranslation();
    const navigate = useNavigate();
    const showMessage = useMessage();

    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);

    const [subscription, setSubscription] = useState(null);
    const [image, setImage] = useState(null);

    const {handleSubmit, control, register, formState: {errors}} = useForm();


    useEffect(() => {
        if (subscriptionId) {
            (async () => {
                setPageLoading(true);
                try {
                    const [_result] = await Promise.all([
                        subscriptionService.get(subscriptionId),
                    ]);
                    initPageValues(_result);
                } catch (e) {
                    console.error(e);
                }
                setPageLoading(false);
            })();
        } else {
            setPageLoading(false);
        }
        return () => {
        }
    }, [subscriptionId]);

    const initPageValues = (_subscription) => {
        setSubscription(_subscription);
        setImage(_subscription?.image);
    }

    const onSubmit = async (data) => {

        if (loading) return false;
        try {
            setLoading(true);

            const obj = {
                id: subscriptionId | null,
                ...data,
                bonus: Boolean(data.bonus),
                image
            }
            const resultObject = await subscriptionService.saveOrUpdate(subscriptionId, obj);
            showMessage(t(subscriptionId ? 'message.updated' : 'message.created'));

            if (!subscriptionId) {
                navigate(`/commons/subscriptions/edit/${resultObject._id}`, {replace: true});
            } else {
                initPageValues(resultObject);
            }

        } catch ({code, message}) {
            showMessage(`${code} - ${message}`, MessageType.ERROR);
        } finally {
            setLoading(false);
        }
    }


    const ActionPanel = () => (
        <PageActions>
            <BackButton loading={loading}/>
            <SaveButton loading={loading}/>
        </PageActions>
    );


    return (
        <Page title={title} loading={pageLoading}>
            <Container maxWidth={"sm"}>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <ActionPanel/>

                    <Panel title={t('title.subscription')}>
                        <ApplicationInput
                            name="application"
                            defaultValue={subscription?.application}
                            control={control}
                            rules={{required: t('validation.required')}}
                            error={errors?.application}
                        />
                        <TranslatableFields
                            entity={subscription}
                            register={register}
                            errors={errors}
                            fields={[
                                {name: "name", required: true},
                                {name: "description", multiline: true},
                            ]}
                        />
                        <Input
                            defaultValue={subscription?.code}
                            label={t('label.code')}
                            error={errors?.code}
                            {...register("code", {
                                required: t('validation.required')
                            })}
                        />
                        <Input
                            defaultValue={subscription?.ios}
                            label={t('label.iosCode')}
                            error={errors?.ios}
                            {...register("ios")}
                        />
                        <Select
                            name="bonus"
                            rules={{required: t('validation.required')}}
                            error={errors?.bonus}
                            defaultValue={Number(subscription?.bonus === 'true')}
                            label={t('label.bonus')}
                            control={control}>
                            <MenuItem value={0}>{t('label.no')}</MenuItem>
                            <MenuItem value={1}>{t('label.yes')}</MenuItem>
                        </Select>

                        <ImagePickerButton {...{image, setImage}} />

                    </Panel>
                    <ActionPanel/>
                </form>

            </Container>
        </Page>
    );

}

export default SubscriptionEditPage;
